import React, { useCallback, useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useParams, useSearchParams, Link } from "react-router-dom"
import { Card, CardBody, CardHeader, Row, Col, Spinner } from "reactstrap"
import GeneralTables from "components/Common/GeneralTable"
import {
  getMonitorInItems,
  getMonitorOutItems,
} from "store/monitor-dashboard/monitor-dashboard-action"
import { monitorDashboardAction } from "store/monitor-dashboard/monitor-dashboard-slice"
import {
  buildFilterParams,
  parseFilterSearchParams,
} from "utils/providerDashboardFilters"

const MonitorProvinceDetail = () => {
  const { provinceId } = useParams()
  const [searchParams] = useSearchParams()
  const dispatch = useDispatch()

  const type = searchParams.get("type") || "receipt"
  const isReceipt = type === "receipt"

  const dashboardFilters = useMemo(
    () => parseFilterSearchParams(searchParams),
    [searchParams]
  )

  const MonitorInItems = useSelector(
    state => state.monitorDashboard.MonitorInItems
  )
  const MonitorOutItems = useSelector(
    state => state.monitorDashboard.MonitorOutItems
  )

  const items = isReceipt ? MonitorInItems : MonitorOutItems
  const data = items?.data?.data || []
  const meta = items?.data?.meta
  const total = items?.data?.total_quantity ?? 0
  const loading = items?.loading

  const buildParams = useCallback(
    (page = 1, perPage = 10) => ({
      province: provinceId,
      type,
      page,
      per_page: perPage,
      ...buildFilterParams(dashboardFilters),
    }),
    [provinceId, type, dashboardFilters]
  )

  useEffect(() => {
    const params = buildParams(1)
    if (isReceipt) {
      dispatch(getMonitorInItems(params))
    } else {
      dispatch(getMonitorOutItems(params))
    }
    return () => {
      if (isReceipt) {
        dispatch(monitorDashboardAction.resetMonitorInItems())
      } else {
        dispatch(monitorDashboardAction.resetMonitorOutItems())
      }
    }
  }, [dispatch, provinceId, isReceipt, buildParams])

  const onPageClick = useCallback(
    link => {
      let page = 1
      if (link?.url) {
        const match = link.url.match(/[?&]page=(\d+)/)
        if (match) {
          page = match[1]
        }
      }
      const params = buildParams(page)
      if (isReceipt) {
        dispatch(getMonitorInItems(params))
      } else {
        dispatch(getMonitorOutItems(params))
      }
    },
    [isReceipt, buildParams, dispatch]
  )

  const provinceName = items?.data?.province_name || `Province #${provinceId}`

  return (
    <div className="page-content">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">{provinceName}</h4>
          <p className="text-muted mb-0">
            {isReceipt ? "Receipt Items" : "Distribution Items"}
          </p>
        </div>
        <Link to="/dashboard" className="btn btn-sm btn-secondary">
          Back to Dashboard
        </Link>
      </div>

      <Row>
        <Col md={12}>
          <Card>
            <CardHeader>
              <h5 className="mb-0">{isReceipt ? "Receipts" : "Distributions"}</h5>
            </CardHeader>
            <CardBody>
              {loading ? (
                <div className="text-center py-4"><Spinner /></div>
              ) : !data?.length ? (
                <div className="text-center py-4 text-muted">No data</div>
              ) : (
                <div>
                  <GeneralTables
                    data={data}
                    loading={loading}
                    headers={[
                      "#",
                      "UNFPA ITEM ID",
                      "IP-ITEM DESCRIPTION",
                      "BATCH NUMBER",
                      "EXPIRE DATE",
                      isReceipt ? "QUANTITY" : "OUT QUANTITY",
                      isReceipt ? "RECEIPTS DATE" : "DISTRIBUTION DATE",
                    ]}
                    pagination={{ meta, onPageClick }}
                    renderItem={(item, idx) => (
                      <tr key={idx}>
                        <th scope="row">{meta?.from ? meta.from + idx : idx + 1}</th>
                        <td>{item.item?.unfp_item_name}</td>
                        <td>{item.item?.ip_item_description}</td>
                        <td>{isReceipt ? item.batch_number : item.provider_in?.batch_number}</td>
                        <td>{isReceipt ? item.expire_date : item.provider_in?.expire_date}</td>
                        <td className="text-end">
                          {isReceipt ? item.first_in_qty_per_ip : item.out_quantity}
                        </td>
                        <td>{item.date}</td>
                      </tr>
                    )}
                  />
                  <div style={{ textAlign: "right", fontSize: 16, fontWeight: 500, padding: "8px 12px" }}>
                    Total: {total.toLocaleString()}
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default MonitorProvinceDetail
